import { useRef, useState, type FormEvent } from "react";
import { Banner, Button, Field, Input, Modal, TextArea, useToast } from "@/shared/rd";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  capturePrivateSession,
  isPrivateSessionCurrent,
} from "@/lib/api/private-session";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useDeleteStoreMutation } from "@/modules/stores/services/storeControls";

type Props = {
  open: boolean;
  onClose: () => void;
  storeId: string;
  storeSlug: string;
  /** Called after the store is gone and its toast is showing (the page navigates away). */
  onDeleted: () => void;
};

/**
 * Delete forever, behind a typed confirmation: the delete button stays off
 * until the store's web address is typed exactly. The server checks the same
 * text again and refuses a store with real activity (409 STORE_HAS_ACTIVITY).
 */
export function StoreDeleteModal({ open, onClose, storeId, storeSlug, onDeleted }: Props) {
  const t = useTranslations();
  const lifetime = useRef(capturePrivateSession()).current;
  const toast = useToast();
  const mutation = useDeleteStoreMutation();
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const matches = typed.trim().toLowerCase() === storeSlug;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!matches) return;
    setError(null);
    try {
      await mutation.mutateAsync({
        storeId,
        confirmSlug: typed.trim(),
        reason: reason.trim() || null,
      });
      if (!isPrivateSessionCurrent(lifetime)) return;
      toast.show({ message: t(keys.storeControls.deleted) });
      onDeleted();
    } catch (caught) {
      if (!isPrivateSessionCurrent(lifetime)) return;
      if (caught instanceof ApiJsonClientError) {
        if (caught.code === "STORE_HAS_ACTIVITY") {
          setError(t(keys.storeControls.deleteBlocked));
          return;
        }
        if (caught.code === "STORE_DELETE_CONFIRMATION_MISMATCH") {
          setError(t(keys.storeControls.deleteConfirmMismatch));
          return;
        }
      }
      setError(
        caught instanceof ApiJsonClientError && caught.message
          ? caught.message
          : t(keys.errors.generic),
      );
    }
  };

  const formId = `store-delete-${storeId}`;

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? () => undefined : onClose}
      title={t(keys.storeControls.deleteTitle)}
      actions={
        <>
          <Button variant="ghost" muted onClick={onClose} disabled={mutation.isPending}>
            {t(keys.common.cancel)}
          </Button>
          <Button type="submit" form={formId} variant="danger" disabled={!matches}
            loading={mutation.isPending}
            loadingLabel={t("admin.busyDeleting")}>
            {t(keys.storeControls.deleteForever)}
          </Button>
        </>
      }
    >
      <form id={formId} style={{ display: "flex", flexDirection: "column", gap: 12 }} onSubmit={submit}>
        {error ? (
          <Banner tone="error" title={t("storeControls.deleteFailedTitle")}>
            {error} {t("admin.nothingChanged")}
          </Banner>
        ) : null}
        <p className="rd-modal-text" style={{ margin: 0 }}>{t(keys.storeControls.deleteBody)}</p>
        <Field label={t(keys.storeControls.deleteConfirmLabel, { slug: storeSlug })}>
          <Input
            name="confirmSlug"
            dir="ltr"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            value={typed}
            placeholder={storeSlug}
            onChange={(e) => setTyped(e.target.value)}
          />
        </Field>
        <Field label={t(keys.storeControls.reason)}>
          <TextArea
            name="deleteReason"
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </form>
    </Modal>
  );
}
